import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Database, CheckCircle, AlertCircle, XCircle } from 'lucide-react';

interface DataSource {
  name: string;
  system: string;  // Generic system type (e.g., "ERP System", "CRM System")
  data_elements: string[];
  status: 'available' | 'needs_validation' | 'gap';
  notes?: string;
  typical_systems?: string[];  // Examples: ["SAP", "Oracle", "Kinaxis"]
}

interface DataSourceAnalysis {
  available: DataSource[];
  needs_validation: DataSource[];
  gaps: DataSource[];
}

interface DataSourcesSectionProps {
  dataSources: DataSourceAnalysis;
  className?: string;
}

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'available':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'needs_validation':
      return <AlertCircle className="w-4 h-4 text-amber-500" />;
    case 'gap':
      return <XCircle className="w-4 h-4 text-red-500" />;
    default:
      return null;
  }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles = {
    available: 'bg-green-100 text-green-700 border-green-200',
    needs_validation: 'bg-amber-100 text-amber-700 border-amber-200',
    gap: 'bg-red-100 text-red-700 border-red-200'
  };

  const labels = {
    available: 'Available',
    needs_validation: 'Needs Validation',
    gap: 'Data Gap'
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[status as keyof typeof styles]}`}>
      {labels[status as keyof typeof labels]}
    </span>
  );
};

const DataSourceCard: React.FC<{ source: DataSource }> = ({ source }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <StatusIcon status={source.status} />
          <div className="text-left">
            <p className="font-medium text-neutral-900 text-sm">{source.name}</p>
            <p className="text-xs text-neutral-500">{source.system}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={source.status} />
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-neutral-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-neutral-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="p-3 pt-0 border-t border-neutral-100 space-y-2">
          {/* Data Elements */}
          {source.data_elements && source.data_elements.length > 0 && (
            <div>
              <p className="text-xs font-medium text-neutral-600 mb-1">Data Elements Needed</p>
              <div className="flex flex-wrap gap-1">
                {source.data_elements.map((element, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-neutral-100 text-neutral-700 text-xs rounded"
                  >
                    {element}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Typical Systems (system-agnostic examples) */}
          {source.typical_systems && source.typical_systems.length > 0 && (
            <div>
              <p className="text-xs font-medium text-neutral-600 mb-1">Typically Found In</p>
              <p className="text-xs text-neutral-500">
                {source.typical_systems.join(', ')}
              </p>
            </div>
          )}

          {/* Notes */}
          {source.notes && (
            <div>
              <p className="text-xs font-medium text-neutral-600 mb-1">Notes</p>
              <p className="text-xs text-neutral-500">{source.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const DataSourcesSection: React.FC<DataSourcesSectionProps> = ({
  dataSources,
  className = ''
}) => {
  if (!dataSources) {
    return null;
  }

  const totalSources =
    (dataSources.available?.length || 0) +
    (dataSources.needs_validation?.length || 0) +
    (dataSources.gaps?.length || 0);

  if (totalSources === 0) {
    return null;
  }

  const readinessScore = dataSources.available?.length
    ? Math.round((dataSources.available.length / totalSources) * 100)
    : 0;

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-neutral-900">Data Sources Required</h3>
        </div>
        <div className="text-sm">
          <span className="text-neutral-500">Data Readiness: </span>
          <span className={`font-medium ${
            readinessScore >= 70 ? 'text-green-600' :
            readinessScore >= 40 ? 'text-amber-600' : 'text-red-600'
          }`}>
            {readinessScore}%
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {/* Available Sources */}
        {dataSources.available && dataSources.available.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <h4 className="text-sm font-medium text-neutral-700">
                Available Data ({dataSources.available.length})
              </h4>
            </div>
            <div className="space-y-2">
              {dataSources.available.map((source, i) => (
                <DataSourceCard key={i} source={source} />
              ))}
            </div>
          </div>
        )}

        {/* Needs Validation */}
        {dataSources.needs_validation && dataSources.needs_validation.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <h4 className="text-sm font-medium text-neutral-700">
                Needs Validation ({dataSources.needs_validation.length})
              </h4>
            </div>
            <div className="space-y-2">
              {dataSources.needs_validation.map((source, i) => (
                <DataSourceCard key={i} source={source} />
              ))}
            </div>
          </div>
        )}

        {/* Data Gaps */}
        {dataSources.gaps && dataSources.gaps.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-4 h-4 text-red-500" />
              <h4 className="text-sm font-medium text-neutral-700">
                Data Gaps ({dataSources.gaps.length})
              </h4>
            </div>
            <div className="space-y-2">
              {dataSources.gaps.map((source, i) => (
                <DataSourceCard key={i} source={source} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Summary Note - System Agnostic */}
      <div className="text-xs text-neutral-500 italic pt-2 border-t border-neutral-100">
        Note: System names are examples of where this data is typically found.
        Actual data sources will depend on your organization's enterprise systems.
      </div>
    </div>
  );
};

export default DataSourcesSection;
